/**
 * move_reconcile.ts — PURE decision logic for reconciling the predicted local
 * avatar against the authoritative `player_state` row (the walking-drag lesson).
 *
 * THE BUG IT REPLACES: the old reconcile compared the row to the client's
 * CURRENT position every tick. The row always lags by the round trip, so at
 * 6 m/s any acknowledgement latency above ~42 ms (0.25 m) read as
 * "divergence" and the avatar was dragged 30 %/tick back toward a stale
 * position — wading through mud on any loaded connection. Vehicles skip
 * reconcile entirely, which is why they glided while walking stuttered.
 *
 * THE FIX (standard client-side prediction): remember the position the
 * client predicted when it SENT each input sequence; when the row acknowledges
 * sequence k, compare the server's position with the prediction FOR k. Latency
 * alone then never produces a correction; only real disagreement (the server
 * clamped a speed, held a position, nudged an unstick, teleported) does.
 *
 * A disagreement is applied ONCE per acknowledgement (the "ice skates" field
 * bug, 2026-08-22, caught by verify:move-loop-sim): the delta re-bases the
 * LOGICAL position and every later prediction, so the same stale ack can
 * never re-apply the same correction tick after tick during a latency spike
 * (that was ~80 ticks × 30 % of 0.3 m = metres of drag-back, then a snap).
 * The VISUAL position catches up through `pending` — the caller moves the
 * rendered avatar by a fraction of the remaining debt per tick (`applySmoothing`)
 * while predictions are always recorded at logical = visual + pending.
 *
 * Node-verifiable (verify:move-reconcile). No three, no SDK, no DOM.
 */

export interface PredictedSample { seq: number; x: number; z: number; /** performance.now() at the send (ack-lag stats) */ t: number }

export interface ReconcileState {
  /** predictions in send order, oldest first (bounded) */
  history: PredictedSample[];
  /** counters for probes */
  stats: { none: number; smooth: number; hard: number; predicted: number; current: number; waiting: number; lastDiv: number; lastAckLagMs: number; maxAckLagMs: number };
  /** consecutive ticks spent waiting for a known ack (a stale link must not wait forever) */
  waitingTicks: number;
  /** visual catch-up debt: logical position = visual + pending */
  pendingX: number;
  pendingZ: number;
}

/** After this many consecutive waiting ticks (~2 s) the current position becomes the basis again. */
export const WAIT_MAX_TICKS = 120;

/** ~34 s of coalesced sends (30 Hz): measured ack lag on a loaded Commons spikes past a second (verify:acklag p99 1.4 s) — the window must dwarf it. */
export const HISTORY_MAX = 1024;
/** Fraction of a real disagreement closed per tick (converges in ~6 ticks). */
export const SMOOTH_GAIN = 0.3;

export const freshReconcile = (): ReconcileState => ({
  history: [],
  stats: { none: 0, smooth: 0, hard: 0, predicted: 0, current: 0, waiting: 0, lastDiv: 0, lastAckLagMs: 0, maxAckLagMs: 0 },
  waitingTicks: 0,
  pendingX: 0,
  pendingZ: 0,
});

/** The logical position (where the server will agree) = visual + the smoothing debt. */
export function logicalX(st: ReconcileState, visualX: number): number { return visualX + st.pendingX; }
export function logicalZ(st: ReconcileState, visualZ: number): number { return visualZ + st.pendingZ; }

/**
 * One tick of visual catch-up: returns the step to add to the rendered
 * position (a fraction of the remaining debt; tiny remainders close fully).
 */
export function applySmoothing(st: ReconcileState, gain = SMOOTH_GAIN): { dx: number; dz: number } {
  const len = Math.hypot(st.pendingX, st.pendingZ);
  if (len < 1e-4) { st.pendingX = 0; st.pendingZ = 0; return { dx: 0, dz: 0 }; }
  const k = len < 0.02 ? 1 : gain;
  const dx = st.pendingX * k, dz = st.pendingZ * k;
  st.pendingX -= dx; st.pendingZ -= dz;
  return { dx, dz };
}

/** Remember where the client stood when it sent `seq` (call right after each send). */
export function recordSend(st: ReconcileState, seq: number, x: number, z: number, tMs = 0): void {
  st.history.push({ seq, x, z, t: tMs });
  if (st.history.length > HISTORY_MAX) st.history.splice(0, st.history.length - HISTORY_MAX);
}

export interface Correction {
  /** none = leave the client alone; smooth = the predicted path was re-based by (dx, dz) and the debt queued; hard = snap to the server position */
  mode: 'none' | 'smooth' | 'hard';
  /** the server's disagreement with the prediction (or with the current position) */
  dx: number;
  dz: number;
  div: number;
  /** which position the server was compared against */
  basis: 'predicted' | 'current' | 'waiting';
}

/**
 * Decide this tick's correction. `localX/Z` are the LOGICAL position (visual + pending).
 *   ackSeq   — the row's inputSeq (the server's acknowledgement)
 *   localSeq — the client's latest sent seq (ackSeq < localSeq ⇒ inputs in flight)
 *   snapM    — divergence above which a smooth correction starts
 *   hardM    — divergence above which the client snaps (a teleport, not drift)
 */
export function decideCorrection(
  st: ReconcileState, ackSeq: number, serverX: number, serverZ: number,
  localX: number, localZ: number, localSeq: number, snapM: number, hardM: number, nowMs = 0,
): Correction {
  // Everything older than the acknowledged sequence is history the server has passed.
  let drop = 0;
  while (drop < st.history.length && st.history[drop].seq < ackSeq) drop++;
  if (drop > 0) st.history.splice(0, drop);

  let basis: Correction['basis'];
  let bx: number, bz: number;
  const predicted = st.history.length > 0 && st.history[0].seq === ackSeq ? st.history[0] : null;
  if (predicted) {
    basis = 'predicted'; bx = predicted.x; bz = predicted.z;
    st.waitingTicks = 0;
    if (nowMs > 0 && predicted.t > 0) { const lag = nowMs - predicted.t; st.stats.lastAckLagMs = lag; if (lag > st.stats.maxAckLagMs) st.stats.maxAckLagMs = lag; }
  } else if (ackSeq >= localSeq) {
    // Nothing in flight (idle, or an ack the client never recorded — a reset):
    // the current position IS the prediction for the acked state.
    basis = 'current'; bx = localX; bz = localZ;
    st.waitingTicks = 0;
  } else {
    // Inputs are in flight but the ack matches no prediction (history trimmed,
    // or a world entry reset the server's sequence).
    // A TELEPORT must still land: if the server stands farther than hardM from
    // EVERY prediction and from the current position, no in-flight input can
    // explain it (predictions trace the walked path) — snap now, whatever is
    // acknowledged. Otherwise wait for a known ack rather than compare a
    // lagging row against a moving client — but never forever: a stale link
    // (acks that never come) falls back to the current basis after WAIT_MAX_TICKS.
    let nearest = Math.hypot(serverX - localX, serverZ - localZ);
    for (const h of st.history) nearest = Math.min(nearest, Math.hypot(serverX - h.x, serverZ - h.z));
    if (nearest > hardM) {
      st.history.length = 0;
      st.pendingX = 0; st.pendingZ = 0;
      st.waitingTicks = 0;
      st.stats.hard++;
      st.stats.lastDiv = nearest;
      return { mode: 'hard', dx: serverX - localX, dz: serverZ - localZ, div: nearest, basis: 'current' };
    }
    if (st.waitingTicks < WAIT_MAX_TICKS) {
      st.waitingTicks++;
      st.stats.waiting++;
      return { mode: 'none', dx: 0, dz: 0, div: 0, basis: 'waiting' };
    }
    // The wait expired — the old behaviour, bounded; stays in force until a known ack arrives.
    basis = 'current'; bx = localX; bz = localZ;
  }
  const dx = serverX - bx, dz = serverZ - bz;
  const div = Math.hypot(dx, dz);
  st.stats.lastDiv = div;
  if (basis === 'predicted') st.stats.predicted++; else st.stats.current++;
  if (div > hardM) {
    // A teleport-scale disagreement: the predictions are meaningless now.
    st.history.length = 0;
    st.pendingX = 0; st.pendingZ = 0;
    st.stats.hard++;
    return { mode: 'hard', dx, dz, div, basis };
  }
  if (div > snapM) {
    // Apply ONCE: re-base the logical path by the disagreement — the acked
    // prediction now equals the server, every later prediction shifts with it,
    // and the visual owes the delta (closed over the next ticks by applySmoothing).
    for (const h of st.history) { h.x += dx; h.z += dz; }
    st.pendingX += dx; st.pendingZ += dz;
    st.stats.smooth++;
    return { mode: 'smooth', dx, dz, div, basis };
  }
  st.stats.none++;
  return { mode: 'none', dx, dz, div, basis };
}
